import React, { useState, useMemo } from "react";
import axios from "axios";
import { X, ExternalLink, ArrowRight, GraduationCap, Wallet, Trophy, Building2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Webinars from "../components/Webinars";
import ApplicationDialog from "../components/ApplicationDialog";
import FloatingContact from "../components/FloatingContact";
import { universities, UNI_NETWORKS } from "../data/universitiesCatalogData";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "../hooks/use-toast";
import { useLang } from "../i18n/LangContext";
import { catalogI18n } from "../i18n/catalog_i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'all', label: 'Все', flag: '🌍' },
  { code: 'UK', label: 'UK', flag: '🇬🇧' },
  { code: 'USA', label: 'USA', flag: '🇺🇸' },
  { code: 'NL', label: 'NL', flag: '🇳🇱' },
  { code: 'CH', label: 'CH', flag: '🇨🇭' },
  { code: 'IE', label: 'IE', flag: '🇮🇪' },
  { code: 'DE', label: 'DE', flag: '🇩🇪' },
  { code: 'AT', label: 'AT', flag: '🇦🇹' },
  { code: 'ES', label: 'ES', flag: '🇪🇸' },
  { code: 'PL', label: 'PL', flag: '🇵🇱' },
  { code: 'CZ', label: 'CZ', flag: '🇨🇿' }
];

const UniCard = ({ uni, onOpen, t }) => {
  const initials = uni.name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 3).join('').toUpperCase();
  return (
  <div className="rounded-2xl overflow-hidden bg-white transition hover:-translate-y-1 hover:shadow-xl group flex flex-col" style={{ border: '1px solid #E8E0D5' }} data-testid={`uni-card-${uni.id}`}>
    <div className="relative h-44 overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #3a4f6e 100%)' }}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(201,169,110,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)' }} />
      <div className="relative text-center px-5">
        <div className="font-display text-5xl transition group-hover:scale-110" style={{ color: '#C9A96E', fontWeight: 900, letterSpacing: '0.05em' }}>{initials}</div>
        <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.85)' }}>{uni.country}</div>
      </div>
      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: '#C9A96E', color: '#1B2A4A' }}>{uni.network}</div>
      {uni.qs && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1" style={{ background: 'rgba(27,42,74,0.9)', color: '#C9A96E', border: '1px solid #C9A96E' }}>
          <Trophy className="w-3 h-3" /> QS {uni.qs}
        </div>
      )}
      <div className="absolute bottom-3 right-3 text-xl">{uni.flag}</div>
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <h3 className="font-display text-base leading-tight" style={{ color: '#1B2A4A', fontWeight: 800 }}>{uni.name}</h3>
      <div className="text-xs mt-1" style={{ color: '#888' }}>{uni.city}, {uni.country}</div>
      <div className="flex flex-wrap gap-3 mt-3 text-xs">
        <span className="inline-flex items-center gap-1.5" style={{ color: '#1B2A4A' }}><GraduationCap className="w-3.5 h-3.5" style={{ color: '#C9A96E' }} /> {uni.degrees}</span>
      </div>
      <div className="text-sm mt-2 font-semibold" style={{ color: '#1B2A4A' }}><Wallet className="w-3.5 h-3.5 inline mr-1" style={{ color: '#C9A96E' }} />{uni.price}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {uni.programs.slice(0, 3).map((p, i) => (
          <span key={i} className="text-[11px] px-2.5 py-1 rounded-full" style={{ background: '#FAF8F5', border: '1px solid #E8E0D5', color: '#555' }}>{p}</span>
        ))}
      </div>
      <button onClick={() => onOpen(uni)} className="mt-4 inline-flex items-center justify-center gap-2 px-4 h-10 rounded-full text-sm font-semibold transition hover:opacity-90" style={{ background: '#FAF8F5', color: '#1B2A4A', border: '1px solid #E8E0D5' }} data-testid={`uni-more-${uni.id}`}>
        {t.moreBtn} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
  );
};

const UniModal = ({ uni, onClose, onOpenForm, t }) => {
  if (!uni) return null;
  const initials = uni.name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 3).join('').toUpperCase();
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(27,42,74,0.7)' }} onClick={onClose} data-testid="uni-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="relative h-56 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #3a4f6e 100%)' }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(201,169,110,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)' }} />
          <div className="relative text-center px-6">
            <div className="font-display text-6xl" style={{ color: '#C9A96E', fontWeight: 900, letterSpacing: '0.05em' }}>{initials}</div>
            <div className="mt-2 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.9)' }}>{uni.country}</div>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)', color: '#1B2A4A' }} data-testid="uni-modal-close">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-4 flex gap-2">
            <div className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: '#C9A96E', color: '#1B2A4A' }}>{uni.network}</div>
            {uni.qs && <div className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ background: 'rgba(27,42,74,0.9)', color: '#C9A96E', border: '1px solid #C9A96E' }}>QS {uni.qs}</div>}
          </div>
        </div>
        <div className="p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{uni.flag}</span>
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{uni.city}, {uni.country}</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.02em' }}>{uni.name}</h2>
          {uni.desc && <p className="mt-4 text-base leading-relaxed" style={{ color: '#3a4f4f' }}>{uni.desc}</p>}

          <div className="grid grid-cols-2 gap-4 mt-6 pt-6" style={{ borderTop: '1px solid #E8E0D5' }}>
            <div className="flex items-start gap-3">
              <GraduationCap className="w-5 h-5 mt-1" style={{ color: '#C9A96E' }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{t.programsLabel}</div>
                <div className="font-display text-sm mt-1" style={{ color: '#1B2A4A', fontWeight: 600 }}>{uni.degrees}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Wallet className="w-5 h-5 mt-1" style={{ color: '#C9A96E' }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{t.priceLabel}</div>
                <div className="font-display text-sm mt-1" style={{ color: '#1B2A4A', fontWeight: 600 }}>{uni.price}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 mt-1" style={{ color: '#C9A96E' }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{t.networkLabel}</div>
                <div className="font-display text-sm mt-1" style={{ color: '#1B2A4A', fontWeight: 600 }}>{uni.network}</div>
              </div>
            </div>
            <a href={uni.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group">
              <ExternalLink className="w-5 h-5 mt-1 transition group-hover:translate-x-0.5" style={{ color: '#C9A96E' }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{t.siteLabel}</div>
                <div className="font-display text-sm mt-1 truncate transition group-hover:opacity-70" style={{ color: '#1B2A4A', fontWeight: 600 }}>{uni.url.replace(/https?:\/\//, '')}</div>
              </div>
            </a>
          </div>

          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#7A6A4E' }}>{t.directionsLabel}</div>
            <div className="flex flex-wrap gap-2">
              {uni.programs.map((p, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full" style={{ background: '#FAF8F5', border: '1px solid #E8E0D5', color: '#1B2A4A' }}>{p}</span>
              ))}
            </div>
          </div>

          <button onClick={() => { onClose(); onOpenForm(); }} className="mt-8 inline-flex items-center gap-2 w-full justify-center px-6 h-12 rounded-full font-semibold text-white transition hover:opacity-90" style={{ background: '#1B2A4A' }}>
            {t.consultBtn} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const UniversitiesCatalogPage = () => {
  const { lang } = useLang();
  const t = (catalogI18n[lang] || catalogI18n.ru).universities;
  const isRtl = lang === 'ar';
  const [formOpen, setFormOpen] = useState(false);
  const [country, setCountry] = useState('all');
  const [network, setNetwork] = useState('all');
  const [active, setActive] = useState(null);

  const [bf, setBf] = useState({ name: '', level: '', major: '', country: '', email: '', phone: '' });
  const setBfField = (k, v) => setBf(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const filtered = useMemo(() => universities.filter(u =>
    (country === 'all' || u.country === country) && (network === 'all' || u.network === network)
  ), [country, network]);

  const segItems = useMemo(() => filtered.filter(u => u.network === 'SEG'), [filtered]);
  const showSegBanner = (network === 'all' || network === 'SEG') && (country === 'all' || country === 'CH') && segItems.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!bf.name || !bf.email) {
      toast({ title: t.errFill, variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: bf.name, phone: bf.phone, email: bf.email, country: bf.country,
        program: bf.major,
        message: `Education level: ${bf.level}\nMajor: ${bf.major}\nCountry: ${bf.country}`,
        lang, source: 'uni-recommendation'
      });
      setSent(true);
      toast({ title: t.ctaSent });
      setBf({ name: '', level: '', major: '', country: '', email: '', phone: '' });
    } catch { toast({ title: t.errRetry, variant: 'destructive' }); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Header onOpenForm={() => setFormOpen(true)} />
      <main className="pt-28 md:pt-36 pb-20" data-testid="universities-catalog-page">
        {/* Hero */}
        <section className="py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
            <h1 className="font-display text-3xl md:text-5xl text-balance leading-tight" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.025em' }}>{t.title}</h1>
            <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: '#666' }}>{t.subtitle}</p>
          </div>
        </section>

        {/* Filters */}
        <section className="pb-8 sticky top-16 md:top-20 z-20 backdrop-blur" style={{ background: 'rgba(250,248,245,0.92)', borderBottom: '1px solid #E8E0D5' }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 space-y-3">
            <div className="flex flex-wrap gap-2 justify-center">
              {COUNTRIES.map(c => (
                <button
                  key={c.code} onClick={() => setCountry(c.code)}
                  className="px-4 h-9 rounded-full text-sm font-semibold transition border inline-flex items-center gap-1.5"
                  style={country === c.code ? { background: '#1B2A4A', color: '#fff', borderColor: '#1B2A4A' } : { background: '#fff', color: '#1B2A4A', borderColor: '#E8E0D5' }}
                  data-testid={`uni-country-${c.code}`}
                >
                  <span>{c.flag}</span> {c.code === 'all' ? t.allCountries : c.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => setNetwork('all')} className="px-4 h-8 rounded-full text-xs font-semibold transition border" style={network === 'all' ? { background: '#C9A96E', color: '#1B2A4A', borderColor: '#C9A96E' } : { background: 'transparent', color: '#888', borderColor: '#E8E0D5' }} data-testid="uni-network-all">{t.allNetworks}</button>
              {UNI_NETWORKS.map(n => (
                <button key={n} onClick={() => setNetwork(n)}
                  className="px-4 h-8 rounded-full text-xs font-semibold transition border"
                  style={network === n ? { background: '#C9A96E', color: '#1B2A4A', borderColor: '#C9A96E' } : { background: 'transparent', color: '#1B2A4A', borderColor: '#E8E0D5' }}
                  data-testid={`uni-network-${n.replace(/\s+/g, '-').toLowerCase()}`}
                >{n}</button>
              ))}
            </div>
          </div>
        </section>

        {/* SEG info banner */}
        {showSegBanner && (
          <section className="py-6">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
              <div className="rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5" style={{ background: '#FAF8F5', border: '2px solid #C9A96E' }} data-testid="seg-banner">
                <div className="w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center" style={{ background: '#1B2A4A', color: '#C9A96E' }}>
                  <span className="font-display font-black text-xl">SEG</span>
                </div>
                <div className="flex-1">
                  <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-1" style={{ color: '#C9A96E' }}>{t.segTitle}</div>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: '#1B2A4A' }}
                     dangerouslySetInnerHTML={{ __html: t.segText.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Grid */}
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="text-sm mb-6" style={{ color: '#7A6A4E' }}>{t.found}: <strong style={{ color: '#1B2A4A' }}>{filtered.length}</strong> {t.foundUnit}</div>
            {filtered.length === 0 ? (
              <div className="text-center py-16" style={{ color: '#888' }}>{t.empty}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map(u => <UniCard key={u.id} uni={u} onOpen={setActive} t={t} />)}
              </div>
            )}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="pt-12 pb-4">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <div className="rounded-3xl p-8 md:p-12 text-center" style={{ background: '#1B2A4A', color: '#fff' }} data-testid="uni-bottom-cta">
              <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: '#C9A96E' }}>{t.ctaKicker}</div>
              <h2 className="font-display text-2xl md:text-4xl" style={{ fontWeight: 900, letterSpacing: '-0.02em' }}>{t.ctaTitle}</h2>
              <p className="mt-4 text-base md:text-lg" style={{ color: '#C9A96E' }}>{t.ctaSubtitle}</p>

              {!sent ? (
                <form onSubmit={submit} className="mt-8 space-y-3 text-left max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaName} *</Label><Input value={bf.name} onChange={e => setBfField('name', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-name" /></div>
                    <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaLevel}</Label><Input value={bf.level} onChange={e => setBfField('level', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-level" /></div>
                  </div>
                  <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaMajor}</Label><Input value={bf.major} onChange={e => setBfField('major', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-major" /></div>
                  <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaCountry}</Label><Input value={bf.country} onChange={e => setBfField('country', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-country" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaEmail} *</Label><Input type="email" value={bf.email} onChange={e => setBfField('email', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-email" /></div>
                    <div><Label className="text-xs" style={{ color: '#C9A96E' }}>{t.ctaPhone}</Label><Input value={bf.phone} onChange={e => setBfField('phone', e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="uni-form-phone" /></div>
                  </div>
                  <button type="submit" disabled={busy} className="w-full h-12 rounded-full font-bold mt-2 inline-flex items-center justify-center gap-2 transition hover:opacity-90" style={{ background: '#C9A96E', color: '#1B2A4A' }} data-testid="uni-form-submit">
                    {busy ? '...' : t.ctaSubmit} <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <div className="mt-8 px-6 py-4 rounded-2xl inline-block" style={{ background: 'rgba(201,169,110,0.12)', border: '1px solid rgba(201,169,110,0.35)', color: '#C9A96E' }} data-testid="uni-form-sent">✓ {t.ctaSent}</div>
              )}
            </div>
          </div>
        </section>
      </main>

      <UniModal uni={active} onClose={() => setActive(null)} onOpenForm={() => setFormOpen(true)} t={t} />

      <Webinars />
      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default UniversitiesCatalogPage;
